import { logger } from './logger';

export type SortDir = 'asc' | 'desc';

function compareValues<TValue>(a: TValue | null | undefined, b: TValue | null | undefined): number {
  try {
    if (a === b) return 0;
    if (a == null) return -1;
    if (b == null) return 1;

    // numeric compare when both are numbers
    if (typeof a === 'number' && typeof b === 'number') {
      return a - b;
    }

    // Date objects - compare time value
    if (a instanceof Date && b instanceof Date) {
      return a.getTime() - b.getTime();
    }

    // fallback to string compare (numeric-aware)
    return String(a).localeCompare(String(b), undefined, { numeric: true });
  } catch (error) {
    logger.warn('Error comparing values in sort', {
      error: error instanceof Error ? error.message : String(error),
      aType: typeof a,
      bType: typeof b
    });
    return 0;
  }
}

export type KeySelector<T> = keyof T | ((item: T) => unknown);

export function sortByKey<T, K extends keyof T>(items: T[], key: KeySelector<T>, dir: SortDir = 'asc'): T[] {
  try {
    if (!Array.isArray(items)) {
      logger.warn('sortByKey received non-array items', { itemsType: typeof items });
      return [];
    }

    const factor = dir === 'asc' ? 1 : -1;
    return [...items].sort((x, y) => {
      try {
        const a = typeof key === 'function' ? (key as (item: T) => unknown)(x) : x[key as K];
        const b = typeof key === 'function' ? (key as (item: T) => unknown)(y) : y[key as K];
        return compareValues<unknown>(a as unknown, b as unknown) * factor;
      } catch (itemError) {
        logger.debug('Error comparing sort items', {
          error: itemError instanceof Error ? itemError.message : String(itemError)
        });
        return 0;
      }
    });
  } catch (error) {
    logger.error('Error in sortByKey', {
      error: error instanceof Error ? error.message : String(error),
      itemsCount: Array.isArray(items) ? items.length : 'unknown',
      direction: dir
    });
    return [...items];
  }
}

export function multiKeySort<T, K extends keyof T>(items: T[], keys: K[], dir: SortDir = 'asc'): T[] {
  try {
    if (!Array.isArray(items)) {
      logger.warn('multiKeySort received non-array items', { itemsType: typeof items });
      return [];
    }

    if (!Array.isArray(keys) || keys.length === 0) {
      logger.warn('multiKeySort received invalid keys', {
        keysType: typeof keys,
        keysLength: Array.isArray(keys) ? keys.length : 'unknown'
      });
      return [...items];
    }

    const factor = dir === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
      try {
        for (const k of keys) {
          const cmp = compareValues<T[K]>(a[k] as T[K], b[k] as T[K]);
          if (cmp !== 0) return cmp * factor;
        }
        return 0;
      } catch (itemError) {
        logger.debug('Error comparing items in multiKeySort', {
          error: itemError instanceof Error ? itemError.message : String(itemError)
        });
        return 0;
      }
    });
  } catch (error) {
    logger.error('Error in multiKeySort', {
      error: error instanceof Error ? error.message : String(error),
      itemsCount: Array.isArray(items) ? items.length : 'unknown',
      keysCount: Array.isArray(keys) ? keys.length : 'unknown',
      direction: dir
    });
    return [...items];
  }
}

export default { sortByKey, multiKeySort };
